/**
 * Phone Carousel — single iPhone-mockup that swipes through multiple screens
 * over the scene duration. Each item line `Title | Subtitle | Color` controls
 * the caption + accent for that screen; media[i] (if uploaded) replaces the
 * fallback content.
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  brandName,
} from "../../reel/sceneShared.js";
import { phoneFrame, screenContent, splitItem } from "./_mockHelpers.js";
import { colors } from "../../../brand/theme.js";

export const phoneCarousel: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);
  const headline = inputs.text ?? "";
  const caption = inputs.subtext ?? brandName;
  const isVertical = ctx.format === "9:16";
  const items = (inputs.items?.length ? inputs.items : ["Suche | Anbieter | primary", "Detail | Bewertungen | accent", "Chat | Direkt | secondary"]).slice(0, 6);
  const parsed = items.map((line) => {
    const [title, sub, colorKey] = splitItem(line);
    const cKey = (colorKey ?? "primary").toLowerCase();
    const accentForScreen = cKey === "accent" ? colors.accent : cKey === "secondary" ? colors.secondary : colors.primary;
    return { title: title ?? "", sub: sub ?? "", accent: accentForScreen };
  });

  const phoneW = isVertical ? 460 : 420;
  const lg = typeSize("lg", ctx.format);
  const sm = typeSize("sm", ctx.format);

  const wrapId = `${clipPrefix}-wrap`;
  const headId = `${clipPrefix}-head`;
  const capId = `${clipPrefix}-cap`;
  const deviceId = `${clipPrefix}-device`;
  const trackId = `${clipPrefix}-track`;
  const dotIds = parsed.map((_, i) => `${clipPrefix}-dot-${i}`);
  const captionIds = parsed.map((_, i) => `${clipPrefix}-cap-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "studio", accent });

  // Jede Slide ist exakt so breit wie der innere Phone-Screen. Statt mit
  // prozentualen Flex-Basen zu arbeiten (die in der Praxis Subpixel-Rundungen
  // einführen und das Foto an den Slide-Rändern minimal beschneiden konnten),
  // legen wir die Slides nebeneinander mit `flex:0 0 100%` aufeinander und
  // shiften den Track in 100%-Schritten — so wird der jeweils sichtbare Slide
  // garantiert exakt auf die Phone-Screen-Breite gemappt.
  const screens = parsed.map((p, i) => {
    const inner = screenContent({ media: inputs.media, mediaIndex: i, accent: p.accent, fallback: i % 3 === 0 ? "feed" : i % 3 === 1 ? "list" : "chat", fallbackTitle: p.title || brandName });
    return `<div style="flex:0 0 100%;width:100%;height:100%;position:relative;overflow:hidden;">${inner}</div>`;
  }).join("");

  const phoneInner = `<div id="${trackId}" style="position:absolute;inset:0;display:flex;width:100%;height:100%;transform:translateX(0);will-change:transform;">${screens}</div>`;

  const captionRow = parsed.map((p, i) => `<div id="${captionIds[i]}" style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;text-align:center;opacity:0;">
    <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 1.1}px;letter-spacing:-0.5px;color:${p.accent};">${escapeHtml(p.title)}</div>
    <div style="font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.7}px;color:${withAlpha(themeColors.fg, 0.78)};">${escapeHtml(p.sub)}</div>
  </div>`).join("");

  const dots = `<div style="display:flex;gap:10px;justify-content:center;align-items:center;">${parsed.map((_, i) => `<div id="${dotIds[i]}" style="width:10px;height:10px;border-radius:9999px;background:${withAlpha(themeColors.fg, 0.25)};"></div>`).join("")}</div>`;

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;display:flex;flex-direction:${isVertical ? "column" : "row"};align-items:center;justify-content:center;gap:${isVertical ? 32 : 80}px;padding:64px;">
    <div style="display:flex;flex-direction:column;align-items:${isVertical ? "center" : "flex-start"};gap:18px;text-align:${isVertical ? "center" : "left"};${isVertical ? "" : "flex:1;max-width:560px;"}">
      <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.78}px;letter-spacing:6px;text-transform:uppercase;color:${accent};">${escapeHtml(caption)}</div>
      ${headline ? `<div id="${headId}" style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.78}px;letter-spacing:-2px;color:${themeColors.fg};line-height:1.02;text-wrap:balance;">${escapeHtml(headline)}</div>` : ""}
      <div id="${capId}" style="position:relative;height:${sm * 2.2}px;width:100%;max-width:520px;">${captionRow}</div>
      ${dots}
    </div>
    <div style="position:relative;">
      ${phoneFrame({ id: deviceId, width: phoneW, accent, inner: phoneInner })}
    </div>
  </div>`;

  const init = [
    headline ? gsapSetLine(`#${headId}`, { opacity: 0, y: 16 }) : "",
    gsapSetLine(`#${deviceId}`, { opacity: 0, y: 40, scale: 0.94 }),
    gsapSetLine(`#${trackId}`, { x: 0 }),
    ...dotIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0.4 })),
  ].filter(Boolean).join("\n");

  const tweens: string[] = [
    t.to(`#${deviceId}`, { opacity: 1, y: 0, scale: 1, duration: 0.85, ease: "raw:\"power2.out\"" }, 0.0),
  ];
  if (headline) tweens.push(t.to(`#${headId}`, { opacity: 1, y: 0, duration: 0.55, ease: "raw:\"power2.out\"" }, 0.2));

  const slotDur = Math.max(0.8, (clipDurationSec - 0.6) / parsed.length);
  parsed.forEach((_, i) => {
    const at = 0.4 + i * slotDur;
    if (i > 0) {
      // Da jetzt jeder Slide 100% des sichtbaren Phone-Screens ist, shiften
      // wir den Track in vollen 100%-Schritten. Vorher war es ein Bruchteil
      // (i*100/N), passend zum überdimensionierten Track — das ist nicht
      // mehr nötig.
      tweens.push(t.to(`#${trackId}`, { x: `-${i * 100}%`, duration: 0.5, ease: "raw:\"power2.inOut\"" }, at));
    }
    tweens.push(t.to(`#${captionIds[i]}`, { opacity: 1, duration: 0.4, ease: "raw:\"power1.out\"" }, at + 0.15));
    if (i + 1 < parsed.length) {
      tweens.push(t.to(`#${captionIds[i]}`, { opacity: 0, duration: 0.3, ease: "raw:\"power1.in\"" }, at + slotDur - 0.2));
    }
    tweens.push(t.to(`#${dotIds[i]}`, { backgroundColor: parsed[i]!.accent, scale: 1.4, duration: 0.3, ease: "raw:\"power2.out\"" }, at));
    if (i + 1 < parsed.length) {
      tweens.push(t.to(`#${dotIds[i]}`, { scale: 1, duration: 0.3, ease: "raw:\"power1.out\"" }, at + slotDur - 0.2));
    }
  });

  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec));
  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
